/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kScopedStyles — CSS isolation layer for custom components in SharePoint pages.
 *
 * Uses a configurable data attribute selector for specificity to beat
 * SharePoint's class-based rules without !important (except box-sizing).
 * Prevents SP's global styles from bleeding into custom components.
 *
 * ⚠ WITH ONE DELIBERATE EXCEPTION, and it is the whole of `ButtonRule` and `InputRule`: the
 * declarations a design system legitimately owns - a control's background, border, padding,
 * font and colour - are emitted so that the APP'S OWN CLASSES WIN, because a reset that
 * outranks `.k-btn` silently un-styles every button in the product. Read the comment on
 * `ButtonRule` before raising any selector in this file: the property most likely to need
 * strength (`appearance`) is still at full specificity, and the ones weakened are re-asserted
 * at (0,2,1) for elements the app has not classed.
 *
 * Usage:
 *   kScopedStyles.Apply(myRootElement, "myapp");
 *   // Sets data-myapp attribute and injects scoped CSS reset
 */

import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "./kStyles";

export class kScopedStyles {

    private static InjectedApps: Record<string, boolean> = {};

    // ── Apply scoped styles to a root element ──────────────────────────

    /**
     * @param InjectReset defaulted TRUE, which is every existing caller's behaviour. FALSE stamps the
     *   attribute and injects NO element reset - for a page with no foreign CSS on it at all, where
     *   the app's own sheet is injected UNSCOPED.
     *
     * ⚠ THE RESET IS A DEFENCE AGAINST A HOST PAGE, AND ON A PAGE WITH NO HOST IT ONLY BEATS THE APP.
     * `[data-app] h1` is (0,1,1); an unscoped brand's bare `h1` is (0,0,1) and its `.k-panel__body`
     * padding (0,1,0) loses to `[data-app] div { padding:0 }`. MEASURED 2026-09-27 in ContractsManager's
     * browser profile: every H1 and H2 rendered 14px ink instead of 24px/15px accent, every panel lost
     * its padding, and the primary button's white label inherited the body grey - dark text on the one
     * blue fill the brand has - because `[data-app] a { color: inherit }` beat `.k-btn--primary`. The
     * manual's screenshots all show it. On a SharePoint page the app sheet is SCOPED, which lifts every
     * app rule by (0,1,0), so the same reset is harmless there - which is why nothing noticed.
     */
    public static Apply = (Root: HTMLElement, AppName: string, InjectReset: boolean = true): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const AttrName: string = "data-" + AppName;
            Root.setAttribute(AttrName, "");
            if (InjectReset === false) { return; }

            if (kScopedStyles.InjectedApps[AppName]) { return; }
            kScopedStyles.InjectedApps[AppName] = true;

            // Opt-in strict-CSP path (kStyles.UseAdoptedSheets): a constructed sheet instead of a <style> element.
            if (kStyles.UseAdoptedSheets === true) {
                kStyles.AddStyles(AttrName + "-styles", kScopedStyles.BuildResetCSS(AttrName));
                return;
            }
            const Style: HTMLStyleElement = document.createElement("style");
            Style.setAttribute(AttrName + "-styles", "");
            Style.textContent = kScopedStyles.BuildResetCSS(AttrName);
            document.head.appendChild(Style);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.Apply");
        }
    };

    // ── Build scoped CSS reset ─────────────────────────────────────────

    private static BuildResetCSS = (AttrName: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const S: string = "[" + AttrName + "]";
            return kScopedStyles.JoinRules(S);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.BuildResetCSS");
            return "";
        }
    };

    private static JoinRules = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Rules: string[] = [
                kScopedStyles.BoxSizingRule(S),
                kScopedStyles.RootRule(S),
                kScopedStyles.HeadingRule(S),
                kScopedStyles.TableRules(S),
                kScopedStyles.ButtonRule(S),
                kScopedStyles.InputRule(S),
                kScopedStyles.SpanRule(S),
                kScopedStyles.DivRule(S),
                kScopedStyles.LinkRule(S),
                kScopedStyles.ImageRule(S)
            ];
            return Rules.join("\n");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.JoinRules");
            return "";
        }
    };

    // ── Individual CSS reset rules ─────────────────────────────────────

    private static BoxSizingRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return S + " *, " + S + " *::before, " + S + " *::after { box-sizing: border-box !important; }";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.BoxSizingRule");
            return "";
        }
    };

    private static RootRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return S + " { line-height: 1.4; -webkit-text-size-adjust: 100%; font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; color: #202330; }";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.RootRule");
            return "";
        }
    };

    private static HeadingRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return S + " h1, " + S + " h2, " + S + " h3, " + S + " h4, " + S + " h5, " + S + " h6 { margin: 0; padding: 0; font-size: inherit; font-weight: inherit; color: inherit; line-height: inherit; }";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.HeadingRule");
            return "";
        }
    };

    private static TableRules = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const TableBase: string = S + " table { border-collapse: collapse; border-spacing: 0; border: none; margin: 0; }";
            const TableParts: string = S + " thead, " + S + " tbody, " + S + " tfoot, " + S + " tr { border: none; background: none; }";
            const TableCells: string = S + " th, " + S + " td { border: none; padding: 0; text-align: left; vertical-align: middle; background: none; font-size: inherit; color: inherit; }";
            return TableBase + "\n" + TableParts + "\n" + TableCells;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.TableRules");
            return "";
        }
    };

    /**
     * ⚠ A RESET THAT BEATS THE APP'S OWN COMPONENT CLASSES IS NOT A BASELINE, IT IS AN OVERRIDE.
     *
     * Until 2026-09-14 this was ONE rule at `[data-app] button` — an attribute plus an element,
     * **specificity (0,1,1)**. A design system's button class is a bare class at **(0,1,0)**, so
     * the reset won wherever both applied, and it won whatever order the two sheets were injected
     * in, because specificity outranks source order. MEASURED in headless Edge against the Langate
     * Graphite core: every button in ContractsManager — `.k-btn--primary` included, the one control
     * that brand spends its single colour on — rendered `rgba(0,0,0,0)` / `0px none` / `0px`
     * padding / inherited grey / 14px, against an intended blue fill, 1px edge, 12px and 13px.
     *
     * The three groups below exist because the properties divide by WHO OWNS THEM:
     *
     *   HYGIENE stays at (0,1,1). `appearance` is the one that must never lose — it is what stops
     *   a button reverting to native chrome — and no design system sets it per-component. Keeping
     *   it strong is what makes the rest safe to weaken.
     *
     *   BASELINE is the same declarations a design system legitimately sets, emitted twice from
     *   ONE string so there is no second copy to drift:
     *     - `:where(...)` contributes ZERO specificity, so ANY authored rule wins — including an
     *       unscoped bare class, which is the case that was broken. It still beats the UA default,
     *       because author origin outranks user-agent origin whatever the specificity.
     *     - `:not([class])` re-asserts the identical declarations at (0,2,1) for a button the app
     *       has NOT classed. That is the population the element selector was really protecting
     *       from a host page's own CSS, and it is still protected — more strongly than before.
     *   A classed button therefore answers to its class; an unclassed one answers to the reset.
     *
     *   MOUSE-FOCUS is `:focus:not(:focus-visible)`, which is what a blanket `outline: none`
     *   was trying to say. The blanket form killed the keyboard ring too: it sat at (0,1,1) — and
     *   (0,2,1) on the input rule — above every `:focus-visible` rule a brand can write, so the
     *   focus affordance could not be restored by any stylesheet. WCAG 2.4.11/2.4.13.
     */
    private static ButtonRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Owned: string = "background: none; border: none; padding: 0; font-family: inherit; font-size: inherit; color: inherit; line-height: inherit; cursor: pointer;";
            const Hygiene: string = S + " button { appearance: none; -webkit-appearance: none; margin: 0; min-width: 0; min-height: 0; }";
            const Baseline: string = ":where(" + S + " button) { " + Owned + " }";
            const Unclassed: string = S + " button:not([class]) { " + Owned + " }";
            const MouseFocus: string = S + " button:focus:not(:focus-visible) { outline: none; }";
            return Hygiene + "\n" + Baseline + "\n" + Unclassed + "\n" + MouseFocus;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.ButtonRule");
            return "";
        }
    };

    /**
     * The same three groups as `ButtonRule`, and the same defect — worse, because a text field's
     * reset carries an ATTRIBUTE selector, so `[data-app] input[type='text']` is **(0,2,1)** and
     * beat even a SCOPED brand rule at (0,2,0). Measured: every field rendered at the inherited
     * 14px rather than the core's 13px on all four of this product's targets, and the 2px accent
     * focus ring never appeared on any of them — including where the product had tried to
     * re-assert it in its own stylesheet, which could not reach above (0,2,1).
     */
    private static InputRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Text: string = S + " input[type='text']";
            const Search: string = S + " input[type='search']";
            const Owned: string = "font-family: inherit; font-size: inherit; color: inherit;";
            const Hygiene: string = Text + ", " + Search + " { appearance: none; -webkit-appearance: none; margin: 0; min-width: 0; min-height: 0; }";
            const Baseline: string = ":where(" + Text + "), :where(" + Search + ") { " + Owned + " }";
            const Unclassed: string = Text + ":not([class]), " + Search + ":not([class]) { " + Owned + " }";
            const MouseFocus: string = Text + ":focus:not(:focus-visible), " + Search + ":focus:not(:focus-visible) { outline: none; }";
            return Hygiene + "\n" + Baseline + "\n" + Unclassed + "\n" + MouseFocus;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.InputRule");
            return "";
        }
    };

    private static SpanRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return S + " span { font-size: inherit; color: inherit; font-weight: inherit; }";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.SpanRule");
            return "";
        }
    };

    private static DivRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return S + " div { margin: 0; padding: 0; }";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.DivRule");
            return "";
        }
    };

    private static LinkRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return S + " a { color: inherit; text-decoration: none; }";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.LinkRule");
            return "";
        }
    };

    private static ImageRule = (S: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return S + " img { max-width: 100%; height: auto; border: none; }";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.ImageRule");
            return "";
        }
    };

    // ── Scope a WHOLE stylesheet under the app's attribute ─────────────

    /**
     * The attribute `Apply` stamps, for an app name. Public so that a caller building a selector —
     * `ScopeCSS`, a host page, a test — derives it instead of re-spelling the "data-" convention.
     */
    public static AttributeNameFor = (AppName: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return "data-" + AppName;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.AttributeNameFor");
            return "";
        }
    };

    /**
     * Rewrite a stylesheet so every rule in it only applies INSIDE the element `Apply` stamped.
     *
     * WHY IT EXISTS. A product whose stylesheet is written for a page it owns styles `:root`,
     * `body`, `*`, `h1` and `a` globally. That is right on its own page and wrong inside a
     * SharePoint page it is a guest on — and the alternative, keeping a second forked copy of the
     * stylesheet for the guest case, means the brand drifts between two files nobody diffs. This
     * transforms the SAME text at runtime instead.
     *
     * WHAT IT DOES, PRECISELY:
     *   - `:root`, `html` and `body` BECOME the scope element. Custom properties declared on
     *     `:root` therefore land on `[data-app]` and INHERIT into everything the app renders,
     *     which is the whole reason the transform works at all.
     *   - every other selector is prefixed with the scope as an ancestor — `*` and bare elements
     *     because they must not leak, and class selectors because prefixing EVERY rule by the same
     *     (0,1,0) keeps the relative specificity of the sheet exactly as its author wrote it.
     *     Scoping only the bare selectors would silently invert pairs the author had ordered:
     *     an unscoped `.nav a` no longer beats a scoped `a:hover`.
     *   - a selector that ALREADY names the scope is left alone, so the transform is idempotent.
     *   - `@media` / `@supports` / `@layer` / `@container` bodies are scoped recursively;
     *     `@font-face` and `@keyframes` are passed through untouched, because their contents are
     *     descriptors and keyframe selectors rather than element selectors.
     *
     * The caller still needs `Apply` to have stamped the attribute on an element that is a genuine
     * ANCESTOR of everything the app renders — a scoped sheet over an unstamped tree matches
     * nothing at all.
     */
    public static ScopeCSS = (CSSText: string, AttrName: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (AttrName === "") { return CSSText; }
            return kScopedStyles.ScopeRules(CSSText, "[" + AttrName + "]");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.ScopeCSS");
            return CSSText;
        }
    };

    /** Walk a rule list, transforming each prelude and recursing into conditional at-rules. */
    private static ScopeRules = (Css: string, Scope: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            let Out: string = "";
            let Rest: string = Css;
            let Open: number = Rest.indexOf("{");
            while (Open >= 0) {
                const Close: number = kScopedStyles.MatchingBrace(Rest, Open);
                if (Close < 0) { return Out + Rest; }
                Out = Out + kScopedStyles.ScopeOneRule(Rest.substring(0, Open),
                    Rest.substring(Open + 1, Close), Scope);
                Rest = Rest.substring(Close + 1);
                Open = Rest.indexOf("{");
            }
            return Out + Rest;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.ScopeRules");
            return Css;
        }
    };

    /** One `<prelude>{<body>}`, rebuilt. Comments in the prelude are carried through in place. */
    private static ScopeOneRule = (Prelude: string, Body: string, Scope: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Bare: string = kScopedStyles.StripComments(Prelude).trim();
            if (Bare.indexOf("@") === 0) {
                if (kScopedStyles.IsConditionalAtRule(Bare) === false) {
                    return Prelude + "{" + Body + "}";
                }
                return Prelude + "{" + kScopedStyles.ScopeRules(Body, Scope) + "}";
            }
            const Cut: number = Prelude.lastIndexOf("*/");
            let Leading: string = "";
            let Selectors: string = Prelude;
            if (Cut >= 0) {
                Leading = Prelude.substring(0, Cut + 2);
                Selectors = Prelude.substring(Cut + 2);
            }
            return Leading + kScopedStyles.ScopeSelectorList(Selectors, Scope) + "{" + Body + "}";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.ScopeOneRule");
            return Prelude + "{" + Body + "}";
        }
    };

    private static ScopeSelectorList = (Selectors: string, Scope: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Trimmed: string = Selectors.replace(/^\s+/, "");
            const Lead: string = Selectors.substring(0, Selectors.length - Trimmed.length);
            const Done: string[] = [];
            let Part: string = "";
            for (Part of kScopedStyles.SplitTopLevelCommas(Selectors)) {
                Done.push(kScopedStyles.ScopeOneSelector(Part, Scope));
            }
            return Lead + Done.join(", ") + " ";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.ScopeSelectorList");
            return Selectors;
        }
    };

    /**
     * The one decision. `:root` / `html` / `body` BECOME the scope element; everything else hangs
     * beneath it. A selector already naming the scope is returned untouched.
     */
    private static ScopeOneSelector = (Selector: string, Scope: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            const Text: string = Selector.trim();
            if (Text === "") { return Text; }
            if (Text.indexOf(Scope) >= 0) { return Text; }
            if (Text.indexOf(":root") === 0) { return Scope + Text.substring(5); }
            if (kScopedStyles.StartsWithElement(Text, "html") === true) {
                return Scope + Text.substring(4);
            }
            if (kScopedStyles.StartsWithElement(Text, "body") === true) {
                return Scope + Text.substring(4);
            }
            return Scope + " " + Text;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.ScopeOneSelector");
            return Selector;
        }
    };

    /** True when Text opens with exactly this element name — `body` yes, `bodycopy` no. */
    private static StartsWithElement = (Text: string, Name: string): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Text.indexOf(Name) !== 0) { return false; }
            return kScopedStyles.IsNameChar(Text.charAt(Name.length)) === false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.StartsWithElement");
            return false;
        }
    };

    private static IsNameChar = (Ch: string): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Ch === "") { return false; }
            return /[A-Za-z0-9_-]/.test(Ch);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.IsNameChar");
            return false;
        }
    };

    /** Only these at-rules wrap ordinary style rules; the rest hold descriptors or keyframes. */
    private static IsConditionalAtRule = (Bare: string): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            const Name: string = Bare.split(/[\s({]/)[0].toLowerCase();
            const Wrapping: string[] = ["@media", "@supports", "@layer", "@container", "@scope"];
            return Wrapping.indexOf(Name) >= 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.IsConditionalAtRule");
            return false;
        }
    };

    private static StripComments = (Text: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return Text.replace(/\/\*[\s\S]*?\*\//g, "");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.StripComments");
            return Text;
        }
    };

    /** Commas that separate selectors — never one inside `:is(...)` or inside a quoted value. */
    private static SplitTopLevelCommas = (Text: string): string[] => {
        if (ks.KErrorMode === true) { return []; }
        try {
            const Parts: string[] = [];
            let Depth: number = 0;
            let Start: number = 0;
            let Index: number = 0;
            let Quote: string = "";
            while (Index < Text.length) {
                const Ch: string = Text.charAt(Index);
                if (Quote !== "") {
                    if (Ch === Quote) { Quote = ""; }
                } else if (Ch === "\"" || Ch === "'") {
                    Quote = Ch;
                } else if (Ch === "(") {
                    Depth = Depth + 1;
                } else if (Ch === ")") {
                    Depth = Depth - 1;
                } else if (Ch === "," && Depth === 0) {
                    Parts.push(Text.substring(Start, Index));
                    Start = Index + 1;
                }
                Index = Index + 1;
            }
            Parts.push(Text.substring(Start));
            return Parts;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.SplitTopLevelCommas");
            return [Text];
        }
    };

    /** The index of the `}` that closes the `{` at Open, ignoring braces in comments and strings. */
    private static MatchingBrace = (Text: string, Open: number): number => {
        if (ks.KErrorMode === true) { return -1; }
        try {
            let Depth: number = 0;
            let Index: number = Open;
            let Quote: string = "";
            while (Index < Text.length) {
                const Ch: string = Text.charAt(Index);
                if (Quote !== "") {
                    if (Ch === Quote) { Quote = ""; }
                } else if (Ch === "\"" || Ch === "'") {
                    Quote = Ch;
                } else if (Ch === "/" && Text.charAt(Index + 1) === "*") {
                    const End: number = Text.indexOf("*/", Index + 2);
                    if (End < 0) { return -1; }
                    Index = End + 1;
                } else if (Ch === "{") {
                    Depth = Depth + 1;
                } else if (Ch === "}") {
                    Depth = Depth - 1;
                    if (Depth === 0) { return Index; }
                }
                Index = Index + 1;
            }
            return -1;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.MatchingBrace");
            return -1;
        }
    };

    // ── Inject app-specific CSS (animations, custom rules) ──────────

    public static InjectAppCSS = (AppName: string, CSSText: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const StyleId: string = "kScopedStyles-app-" + AppName;
            // Opt-in strict-CSP path (kStyles.UseAdoptedSheets): a constructed sheet instead of a <style> element.
            if (kStyles.UseAdoptedSheets === true) {
                kStyles.AddStyles(StyleId, CSSText);
                return;
            }
            if (document.getElementById(StyleId) !== null) { return; }
            const Style: HTMLStyleElement = document.createElement("style");
            Style.id = StyleId;
            Style.textContent = CSSText;
            document.head.appendChild(Style);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kScopedStyles.InjectAppCSS");
        }
    };
}
